<template>
  <div id="app">
    <section class="newsIndex">
      <div class="container">
        <div class="inner">
          <div class="aside">
            <div class="filters">
              <RenderResources
                :indexName="searchIndex"
                :endPoint="apidomain"
                :filteryear="filteryear"
                :ocpApimSubscriptionKey="ocpApimSubscriptionKey"
                :customHeaders="customHeaders"
                :parentid="parentid"
                :page="1"
                :filtersRequired="1"
              >
                <template slot-scope="{ result, loading, error }">
                  <div class="formContainer">
                    <div class="error" v-if="error">
                      {{ $t("LoadingError") }}
                    </div>
                    <div class="loading" v-if="loading && !error">
                      <p>{{ $t("LoadingFilters") }}</p>
                    </div>
                    <Filters
                      :tagGroups="result.facets"
                      :showYearFilter="filteryear"
                      @change="updateTags"
                      @fetchNews="resourcesChanged"
                      @changeDate="updateYearTags"
                      :tags="tags"
                      :datetags="datetags"
                      :filtergroupids="filtergroupids.split(',')"
                    ></Filters>
                  </div>
                </template>
              </RenderResources>
            </div>
          </div>

          <div class="newsItems">
            <RenderResources
              :parentid="parentid"
              :indexName="searchIndex"
              :endPoint="apidomain"
              :tags="selectedTags"
              :years="selectedYears"
              :page="currentPage"
              :keyword="keywordInput"
              :ocpApimSubscriptionKey="ocpApimSubscriptionKey"
              :customHeaders="customHeaders"
              :pagesize="pagesize"
              :sortorder="[orderBy]"
              @change="resourcesChanged"
            >
              <template v-slot="{ result, loading, error }">
                <div class="content">
                  <div class="error" v-if="error">
                    {{ $t("LoadingResourceError") }}
                  </div>
                  <div class="loading" v-if="loading && !error">
                    <p>{{ $t("LoadingResults") }}</p>
                  </div>
                  <div class="resultsTop">
                    <ResultsIndicator
                      v-if="
                        selectedTags.length > 0 ||
                          selectedYears.length > 0 ||
                          result.count > 0
                      "
                      :first="firstPage"
                      :term="keywordInput"
                      :last="lastPage(result.count)"
                      :total="result.count"
                      :indexTitle="title"
                    ></ResultsIndicator>
                  </div>
                  <div
                    class="resultsContainer"
                    v-if="
                      !loading &&
                        !error &&
                        result.documents !== undefined &&
                        result.documents.length > 0
                    "
                  >
                    <component
                      :is="resultType"
                      v-for="responseValueItem in result.documents"
                      :key="responseValueItem.id"
                      :result="responseValueItem"
                    ></component>
                  </div>

                  <div
                    class="noResults"
                    v-if="
                      !loading &&
                        !error &&
                        (result === undefined || result.count === 0)
                    "
                  >
                    <p>{{ $t("NoResults") }}</p>
                  </div>
                </div>
              </template>
            </RenderResources>
            <div class="pagination" v-if="totalPages > 1">
              <paginate
                :page-count="totalPages"
                :click-handler="paginationClick"
                :prev-text="'Previous'"
                :next-text="'Next'"
                :container-class="'className'"
              ></paginate>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import Paginate from "vuejs-paginate";

import Vue from "vue";
import Filters from "./components/Filters.vue";
import ResultsIndicator from "./components/ResultsIndicator.vue";
import ResultNews from "./components/ResultNews.vue";
import { GenericSearchRequestTaxonomyItem, GenericSearchResponseFacetsCreatedItem, AzureSearchResponse } from "./client/src/models/index";
import RenderResources from "./components/RenderResources.vue"
import { ExtendedTaxonomyTag } from "./models/ExtendedTaxonomyTag";

export default Vue.extend({
  name: "SearchIndex",
  props: {
    filtergroupids: {
      type: String,
      required: true,
      default: ""
    },
    filteryear: {
      type: Boolean,
      required: true,
      default: true
    },
    parentid: {
      type: Number,
      required: true,
      default: -1
    },
    pagesize: {
      type: Number,
      required: true,
      default: 20
    },
    title: {
      type: String,
      required: true,
      default: ""
    },
    description: {
      type: String,
      required: true,
      default: ""
    },
    resultType: {
      type: String,
      required: true,
      default: ""
    },
    ocpApimSubscriptionKey: {
      type: String,
      required: false,
      default: "",
    },
    apidomain: {
      type: String,
      required: false,
      default: "",
    },
    searchIndex: {
      type: String,
      required: false,
      default: "",
    }
  },
  data() {
    return {
      currentPage: 1 as number,
      sortOptions: ["A-Z", "Z-A"] as string[],
      sortOption: "A-Z" as string,
      customHeaders: {"Accept": "application/json;v=1"} as Record<string, any>,
      selectedTags: [],
      selectedYears: [],
      orderBy: "created desc" as string,
      totalPages: Number,
      tags: [] as ExtendedTaxonomyTag[],
      datetags: [] as GenericSearchResponseFacetsCreatedItem[],
    };
  },
  computed: {
    firstPage(): number {
      return (this.currentPage - 1) * this.pagesize + 1;
    }
  },
  methods: {
    lastPage(total: number): number {
      return this.currentPage * this.pagesize > total
        ? total
        : this.currentPage * this.pagesize;
    },
    calculateTotalPages(total: number): number {
      return Math.ceil(total / this.pagesize);
    },
    paginationClick(page: number) {
      window.scrollTo(0, 0);
      const elmnt = document.getElementById("resources");
      if (elmnt) {
        elmnt.scrollIntoView();
      }

      this.$data.currentPage = page;
    },
    convertTagtoSearchTag(
      tag: ExtendedTaxonomyTag
    ): GenericSearchRequestTaxonomyItem {
      const response: GenericSearchRequestTaxonomyItem = {
        tagId: tag.id,
        categoryId: tag.categoryId
      };
      return response;
    },
    updateTags(tags: ExtendedTaxonomyTag[]) {
      this.$data.currentPage = 1;
      this.$data.selectedTags = tags.map((x) => this.convertTagtoSearchTag(x));
      this.resourcesChanged();
    },
    updateYearTags(
      tags: GenericSearchResponseFacetsCreatedItem[]
    ) {
      this.$data.currentPage = 1;
      this.$data.selectedYears = tags.map((x) => x.value?.getFullYear());
      this.resourcesChanged();
    },
    resourcesChanged(update: AzureSearchResponse) {
      const total = update && update.count !== undefined ? update.count : 0;
      this.$data.totalPages = this.calculateTotalPages(total);
    }
  },

  components: {
    Filters,
    ResultsIndicator,
    ResultNews,
    Paginate,
    RenderResources
  }
});
</script>
